import React, { useContext } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import styles from "styles/modules/importElements.module.scss";
import infIcon from "img/infoIcon.svg";
import PrimaryButton from "components/PrimaryButton";
import pluseIconWhite from "img/pluseIconWhite.svg";
import BluePlusIcon from "img/pluseIcon.svg";
import OutlineButton from "components/OutlineButton";
import TextElementCard from "components/modules/TextElementCard";
import { ModuleContext, moduleActionTypes } from "contexts/ModuleContext";
import styled from "styled-components";
import OverlayPopOver from "Uitls/OverlayPopOver";
import { ToolTipText } from "components/TooltipText";
import { moduleScreens } from "pages/module/ModuleUtil";
import ImportClaimsInfoHoverText from "components/modules/hoverTexts/ImportClaimsInfoHoverText";
import ImportRtsInfoHoverText from "components/modules/hoverTexts/ImportRtsInfoHoverText";
import { reorderClaims } from "services/apis";
import { showError } from "Uitls/Util";

const AddedTextElementsList = ({
  elementType,
  elementLabel,
  elements,
  onImportClick,
}) => {
  const { module, updateModule } = useContext(ModuleContext);

  const handleClick = () => {
    onImportClick(elementType);
  };

  const handleDragEnd = (result) => {
    // Only allow reordering for claims
    if (!result.destination || elementType !== moduleScreens.claims) {
      return;
    }

    // Prevent reordering if module is Available for Use
    if (
      module?.mlrStatus &&
      module?.mlrStatus.toLowerCase() === "available for use"
    ) {
      return;
    }

    const { source, destination } = result;

    if (source.index === destination.index) {
      return;
    }

    // Create new array with reordered claims
    const newElements = Array.from(elements);
    const [reorderedElement] = newElements.splice(source.index, 1);
    newElements.splice(destination.index, 0, reorderedElement);

    // Call the reorder API for claims
    const reorderPayload = newElements.map((element, index) => ({
      claimOrder: (index + 1).toString(),
      claimId: element.systemId,
    }));

    reorderClaims(module.moduleId, reorderPayload, (response, error) => {
      if (response) {
        // Convert the reordered elements back to the original claim format
        const reorderedClaims = newElements.map((element, index) => {
          // Find the original claim data from module.claims
          const originalClaim = module.claims.find(
            (claim) => claim.claimId === element.systemId
          );
          return {
            ...originalClaim,
            // Update the order if needed
            claimOrder: (index + 1).toString(),
          };
        });

        // Update the module state with the reordered claims
        updateModule(moduleActionTypes.REORDER_CLAIMS, reorderedClaims);
      } else {
        showError(error);
      }
    });
  };

  return (
    <div className={`${styles.elementxBox}`}>
      <div className="d-flex align-items-center justify-content-between mb-12">
        <div className={`${styles.title}`}>
          <div className="d-flex align-items-center">
            <span>{elementLabel}(s)</span>{" "}
            <OverlayPopOver
              element={<img src={infIcon} alt="Information" />}
              overlayData={
                <ToolTipText>
                  {elementType === moduleScreens.claims ? (
                    <ImportClaimsInfoHoverText />
                  ) : (
                    <ImportRtsInfoHoverText />
                  )}
                </ToolTipText>
              }
              trigger={["hover", "focus"]}
              placement="bottom"
              classId="tooltip"
              inlineStyles={{ maxWidth: "354px" }}
            />
            {elements?.length > 0 ? (
              <span className={`${styles.countRounded}`}>
                {elements?.length}
              </span>
            ) : null}
          </div>
        </div>
        {elements?.length > 0 ? (
          <div>
            <PrimaryButton
              title="Add"
              icon={pluseIconWhite}
              minWidth={100}
              marginLeft={20}
              customClass="text-uppercase"
              handleClick={handleClick}
              disabled={
                module?.mlrStatus &&
                module?.mlrStatus.toLowerCase() === "available for use"
              }
            />
          </div>
        ) : null}
      </div>
      {elements?.length === 0 ? (
        <OutlineButton
          icon={BluePlusIcon}
          btnLabel={`Add ${elementLabel}`}
          handleClick={handleClick}
          disabled={
            module?.mlrStatus &&
            module?.mlrStatus.toLowerCase() === "available for use"
          }
        />
      ) : null}
      {elementType === moduleScreens.claims && elements?.length > 0 ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="claims-list">
            {(provided, snapshot) => (
              <Cards
                className="customScrollBar"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {elements?.map((element, index) => (
                  <Draggable
                    key={element.systemId}
                    draggableId={element.systemId}
                    index={index}
                    isDragDisabled={
                      module?.mlrStatus &&
                      module?.mlrStatus.toLowerCase() === "available for use"
                    }
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                          cursor:
                            module?.mlrStatus &&
                            module?.mlrStatus.toLowerCase() ===
                              "available for use"
                              ? "not-allowed"
                              : "grab",
                        }}
                      >
                        <TextElementCard
                          textElement={element}
                          elementType={elementType}
                          isDraggable={
                            !(
                              module?.mlrStatus &&
                              module?.mlrStatus.toLowerCase() ===
                                "available for use"
                            )
                          }
                          isDragging={snapshot.isDragging}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Cards>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <Cards className="customScrollBar">
          {elements?.map((element) => {
            return (
              <TextElementCard
                textElement={element}
                elementType={elementType}
                isDraggable={false}
              />
            );
          })}
        </Cards>
      )}
    </div>
  );
};

const Cards = styled.div`
  overflow-y: scroll;
  overflow-x: hidden;
  height: calc(100% - 45px);
  margin-right: -13px;
  padding-right: 10px;
`;

export default AddedTextElementsList;
